<template>
  <Teleport to="body">
    <div v-if="visible === true" class="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-50 px-4 py-6" @click="emitClose">
      <div class="w-full max-w-2xl rounded-xl bg-base-100 shadow-2xl" @click.stop>
        <div class="flex items-center justify-between border-b border-base-300 px-6 py-4">
          <h3 class="text-lg font-bold" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.title', 'Voice Settings') }}</h3>
          <button type="button" class="btn btn-ghost btn-sm" @click="emitClose">X</button>
        </div>
        <div class="max-h-[75vh] overflow-y-auto px-6 py-5">
          <div class="form-control w-full mb-4">
            <label class="label">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.defaultEngineVersion', 'Voice version') }}:</span>
            </label>
            <CustomDropdownSelector
              :modelValue="defaultEngineVersion"
              :options="defaultEngineVersionOptions"
              @update:modelValue="emit('update:defaultEngineVersion', $event)"
            />
          </div>
          <VoiceSettingsFields
            :voiceType="voiceType"
            :vadEngine="vadEngine"
            :selectedVoiceId="selectedVoiceId"
            :accent="accent"
            :language="language"
            :languageOptions="languageOptions"
            :selectedTermsPackages="selectedTermsPackages"
            :speed="speed"
            :customPacingEnabled="customPacingEnabled"
            :pacing="pacing"
            :durationStretch="durationStretch"
            :useDashPauses="useDashPauses"
            :pauseEveryWords="pauseEveryWords"
            :dashPauseMs="dashPauseMs"
            :commaPauseMs="commaPauseMs"
            :periodPauseMs="periodPauseMs"
            :idleAutoResponseEnabled="idleAutoResponseEnabled === true"
            :startingMessageIdleAutoResponseSeconds="startingMessageIdleAutoResponseSeconds"
            :idleAutoResponseSeconds="idleAutoResponseSeconds"
            :toolUseAutoResponseSeconds="toolUseAutoResponseSeconds"
            :promptToVoiceEnabled="promptToVoiceEnabled"
            :promptToVoicePrompt="promptToVoicePrompt"
            :tone="tone"
            :toneOptions="toneOptions"
            :mode="mode"
            :modeOptions="modeOptions"
            :cancellingMode="cancellingMode"
            :cancellingModeOptions="cancellingModeOptions"
            :audioProcessorEngine="audioProcessorEngine"
            :audioProcessorEngineOptions="audioProcessorEngineOptions"
            :fillerSentences="fillerSentences"
            :isRtl="isRtl === true"
            :collapseAdvanced="true"
            :hideProvider="true"
            :hideAdvancedEngineSelectors="defaultEngineVersion === 'v5'"
            :voiceFilterMode="defaultEngineVersion === 'v3Flash' ? 'v3Flash' : (defaultEngineVersion === 'v3Duplex' ? 'v3Duplex' : '')"
            @update:voiceType="emit('update:voiceType', $event)"
            @update:vadEngine="emit('update:vadEngine', $event)"
            @update:selectedVoiceId="emit('update:selectedVoiceId', $event)"
            @update:accent="emit('update:accent', $event)"
            @update:language="emit('update:language', $event)"
            @update:selectedTermsPackages="emit('update:selectedTermsPackages', $event)"
            @update:selectedTerms="emit('update:selectedTerms', $event)"
            @update:speed="emit('update:speed', $event)"
            @update:customPacingEnabled="emit('update:customPacingEnabled', $event)"
            @update:pacing="emit('update:pacing', $event)"
            @update:durationStretch="emit('update:durationStretch', $event)"
            @update:useDashPauses="emit('update:useDashPauses', $event)"
            @update:pauseEveryWords="emit('update:pauseEveryWords', $event)"
            @update:dashPauseMs="emit('update:dashPauseMs', $event)"
            @update:commaPauseMs="emit('update:commaPauseMs', $event)"
            @update:periodPauseMs="emit('update:periodPauseMs', $event)"
            @update:idleAutoResponseEnabled="emit('update:idleAutoResponseEnabled', $event)"
            @update:startingMessageIdleAutoResponseSeconds="emit('update:startingMessageIdleAutoResponseSeconds', $event)"
            @update:idleAutoResponseSeconds="emit('update:idleAutoResponseSeconds', $event)"
            @update:toolUseAutoResponseSeconds="emit('update:toolUseAutoResponseSeconds', $event)"
            @update:promptToVoiceEnabled="emit('update:promptToVoiceEnabled', $event)"
            @update:promptToVoicePrompt="emit('update:promptToVoicePrompt', $event)"
            @update:tone="emit('update:tone', $event)"
            @update:mode="emit('update:mode', $event)"
            @update:cancellingMode="emit('update:cancellingMode', $event)"
            @update:audioProcessorEngine="emit('update:audioProcessorEngine', $event)"
            @update:fillerSentences="emit('update:fillerSentences', $event)"
          />
          <div
            :id="'voice-settings-quick-response-filler-' + defaultEngineVersion"
            class="mb-4 rounded-box border border-base-300 bg-base-100 p-4 voice-settings-quick-response-filler"
            :data-voice-engine="defaultEngineVersion"
          >
            <label class="label cursor-pointer px-0 pt-0">
              <span class="label-text font-semibold" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.quickResponseFillerTitle', 'Instant response filler') }}</span>
              <input type="checkbox" class="toggle toggle-primary" :checked="quickResponseFillerEnabled === true" @change="emit('update:quickResponseFillerEnabled', $event.target.checked)" />
            </label>
            <p class="mt-1 text-xs text-base-content/60" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.quickResponseFillerDescription', 'Play a short word immediately after the caller stops speaking while the answer is prepared.') }}</p>
            <div v-if="quickResponseFillerEnabled === true" class="mt-3">
              <label class="label px-0">
                <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.quickResponseFillerText', 'Filler word') }}</span>
              </label>
              <input
                type="text"
                maxlength="80"
                class="input input-bordered w-full"
                :value="quickResponseFillerText"
                :placeholder="t('aiChatAgentSetup.voiceSettings.quickResponseFillerPlaceholder', 'היי')"
                @input="emit('update:quickResponseFillerText', $event.target.value)"
              />
            </div>
          </div>
          <TranscriptionOptimizerSettings
            :enabled="transcriptionOptimizerEnabled === true"
            :terms="transcriptionOptimizerTerms"
            :sourcePrompt="transcriptionOptimizerSourcePrompt"
            :language="language"
            :providerLabel="defaultEngineVersion === 'v3Flash' ? 'Inworld' : ''"
            :isRtl="isRtl === true"
            @update:enabled="emit('update:transcriptionOptimizerEnabled', $event)"
            @update:terms="emit('update:transcriptionOptimizerTerms', $event)"
          />
        </div>
        <div class="flex justify-end border-t border-base-300 px-6 py-4">
          <button type="button" class="btn mr-2" @click="emitClose">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button type="button" class="btn" @click="emitConfirm">{{ t('aiChatAgentSetup.buttons.save', 'Save') }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from '@/i18n';
import VoiceSettingsFields from './VoiceSettingsFields.vue';
import TranscriptionOptimizerSettings from './TranscriptionOptimizerSettings.vue';
import CustomDropdownSelector from './CustomDropdownSelector.vue';

const props = defineProps({
  visible: { type: Boolean, default: false },
  voiceType: { type: String, default: 'vtv' },
  defaultEngineVersion: { type: String, default: 'v1' },
  defaultEngineVersionOptions: { type: Array, default: () => [] },
  vadEngine: { type: String, default: 'tiger' },
  selectedVoiceId: { type: String, default: '' },
  accent: { type: String, default: 'Israeli' },
  language: { type: String, default: 'English' },
  languageOptions: { type: Array, default: () => [] },
  selectedTermsPackages: { type: Array, default: () => [] },
  speed: { type: Number, default: 1 },
  customPacingEnabled: { type: Boolean, default: false },
  pacing: { type: Number, default: 1.05 },
  durationStretch: { type: Number, default: 1 },
  useDashPauses: { type: Boolean, default: true },
  pauseEveryWords: { type: Number, default: 0 },
  dashPauseMs: { type: Number, default: 180 },
  commaPauseMs: { type: Number, default: 120 },
  periodPauseMs: { type: Number, default: 260 },
  idleAutoResponseEnabled: { type: Boolean, default: true },
  startingMessageIdleAutoResponseSeconds: { type: Number, default: 6 },
  idleAutoResponseSeconds: { type: Number, default: 3 },
  toolUseAutoResponseSeconds: { type: Number, default: 2 },
  promptToVoiceEnabled: { type: Boolean, default: false },
  promptToVoicePrompt: { type: String, default: '' },
  tone: { type: String, default: 'Neutral' },
  toneOptions: { type: Array, default: () => [] },
  mode: { type: String, default: 'interruptible' },
  modeOptions: { type: Array, default: () => [] },
  cancellingMode: { type: String, default: 'fast' },
  cancellingModeOptions: { type: Array, default: () => [] },
  audioProcessorEngine: { type: String, default: 'quail' },
  audioProcessorEngineOptions: { type: Array, default: () => [] },
  fillerSentences: { type: Object, default: () => ({}) },
  quickResponseFillerEnabled: { type: Boolean, default: false },
  quickResponseFillerText: { type: String, default: '' },
  transcriptionOptimizerEnabled: { type: Boolean, default: false },
  transcriptionOptimizerTerms: { type: Array, default: () => [] },
  transcriptionOptimizerSourcePrompt: { type: String, default: '' },
  isRtl: { type: Boolean, default: false }
});

const emit = defineEmits([
  'update:voiceType',
  'update:defaultEngineVersion',
  'update:vadEngine',
  'update:selectedVoiceId',
  'update:accent',
  'update:language',
  'update:selectedTermsPackages',
  'update:selectedTerms',
  'update:speed',
  'update:customPacingEnabled',
  'update:pacing',
  'update:durationStretch',
  'update:useDashPauses',
  'update:pauseEveryWords',
  'update:dashPauseMs',
  'update:commaPauseMs',
  'update:periodPauseMs',
  'update:idleAutoResponseEnabled',
  'update:startingMessageIdleAutoResponseSeconds',
  'update:idleAutoResponseSeconds',
  'update:toolUseAutoResponseSeconds',
  'update:promptToVoiceEnabled',
  'update:promptToVoicePrompt',
  'update:tone',
  'update:mode',
  'update:cancellingMode',
  'update:audioProcessorEngine',
  'update:fillerSentences',
  'update:quickResponseFillerEnabled',
  'update:quickResponseFillerText',
  'update:transcriptionOptimizerEnabled',
  'update:transcriptionOptimizerTerms',
  'close',
  'confirm'
]);

const { t } = useI18n();

const labelStyle = computed(() => props.isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' });
const isRtl = computed(() => props.isRtl === true);

function emitClose() {
  emit('close');
}

function emitConfirm() {
  emit('confirm');
}

function handleWindowKeydown(event) {
  if (props.visible !== true) {
    return;
  }
  if (event.key === 'Escape') {
    emitClose();
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleWindowKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleWindowKeydown);
});
</script>
